import { Controller } from "@hotwired/stimulus"

// Multi-file picker for a `multiple: true` file attribute (Forms::Base).
//
// Two things the single-file `file-upload` controller deliberately does not do:
//
// 1. Accumulate. A native <input type="file" multiple> REPLACES its whole
//    selection on every pick, and a phone camera hands back one photo per trip
//    — so without this, "take another photo" would silently discard the first.
//    We keep our own File list and rebuild input.files from a DataTransfer.
// 2. Remove one item. A new pick is spliced out of the list; an already-saved
//    attachment instead appends a hidden <field>_remove[] input carrying its
//    id, which Forms::Base#purge_marked_attachments acts on at update time.
export default class extends Controller {
  static targets = ["input", "grid", "removals", "template", "counter", "label"]
  static values = { max: Number, removeName: String }

  connect() {
    this.picked = []
    this.urls = []
    this.overflow = false
    this.render()
  }

  disconnect() {
    this.#revokeAll()
  }

  pick() {
    const room = Math.max(this.maxValue - this.#keptCount() - this.picked.length, 0)
    const incoming = Array.from(this.inputTarget.files)
    this.picked.push(...incoming.slice(0, room))
    this.overflow = incoming.length > room
    this.#syncInput()
    this.render()
  }

  removeNew(event) {
    const item = event.currentTarget.closest("[data-index]")
    this.picked.splice(Number(item.dataset.index), 1)
    this.overflow = false
    this.#syncInput()
    this.render()
    this.#notifyChanged()
  }

  // Saved attachments are not in input.files — mark the id for the server and
  // drop the tile.
  removeExisting(event) {
    const item = event.currentTarget.closest("[data-attachment-id]")
    const field = document.createElement("input")
    field.type = "hidden"
    field.name = this.removeNameValue
    field.value = item.dataset.attachmentId
    this.removalsTarget.appendChild(field)
    item.remove()
    this.overflow = false
    this.render()
    this.#notifyChanged()
  }

  render() {
    this.gridTarget.querySelectorAll("[data-index]").forEach(el => el.remove())
    this.#revokeAll()

    this.picked.forEach((file, index) => {
      const item = this.templateTarget.content.firstElementChild.cloneNode(true)
      item.dataset.index = index
      item.querySelector("[data-name]").textContent = file.name

      const thumb = item.querySelector("[data-thumb]")
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file)
        this.urls.push(url)
        thumb.src = url
      } else {
        thumb.remove()
      }

      this.gridTarget.appendChild(item)
    })

    const total = this.#keptCount() + this.picked.length
    this.counterTarget.textContent = this.overflow
      ? `Only ${this.maxValue} files allowed — the extra files were not added.`
      : `${total} of ${this.maxValue} files`
    this.labelTarget.classList.toggle("hidden", total >= this.maxValue)
  }

  #keptCount() {
    return this.gridTarget.querySelectorAll("[data-attachment-id]").length
  }

  #syncInput() {
    const transfer = new DataTransfer()
    this.picked.forEach(file => transfer.items.add(file))
    this.inputTarget.files = transfer.files
  }

  #revokeAll() {
    this.urls.forEach(url => URL.revokeObjectURL(url))
    this.urls = []
  }

  // Neither a DOM removal nor a programmatic input.files assignment fires a
  // native event, so dispatch one ourselves — a harmless "the form changed"
  // signal for anything listening (mirrors repeatable_entries_controller#remove).
  // No current consumer needs it (autosave is singleton-only; both forms that
  // use this controller are :open), but it costs nothing to keep correct.
  #notifyChanged() {
    this.element.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
